<template>
  <div class="domains-donut">
    <canvas ref="chartCanvas"></canvas>
  </div>
</template>

<script>
import {
  Chart,
  DoughnutController,
  ArcElement,
  Tooltip,
  Legend,
} from 'chart.js'
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'

Chart.register(
    DoughnutController,
    ArcElement,
    Tooltip,
    Legend,
)

const FALLBACK_COLORS = [
  '#A29BFE',
  '#71E5E0',
  '#D5DBE2',
  '#FF9F40',
  '#F55565',
  '#21AC0C',
  '#8BD05B',
  '#B45CFF',
  '#5C7CFA',
  '#46C7D8',
]

const KEY_COLOR_MAP = {
  drivers: '#A29BFE',
  strategists: '#71E5E0',
  balanced: '#D5DBE2',
  green: '#21AC0C',
  other: '#D5DBE2',
  red: '#F55565',
  highest: '#21AC0C',
  high: '#8BD05B',
  medium: '#FF9F40',
  low: '#F55565',
}

export default {
  name: 'DomainsDonutChart',

  props: {
    items: {
      type: Array,
      default: () => [],
    },
  },

  setup(props) {
    const chartCanvas = ref(null)
    let chartInstance = null

    const toNumber = (value) => {
      const number = Number(value)
      return Number.isFinite(number) ? number : 0
    }

    const getColor = (item, index) => {
      if (item.color) {
        return item.color
      }

      if (item.key && KEY_COLOR_MAP[item.key]) {
        return KEY_COLOR_MAP[item.key]
      }

      return FALLBACK_COLORS[index % FALLBACK_COLORS.length]
    }

    const normalizedItems = computed(() => {
      return props.items.map((item, index) => ({
        ...item,
        value: toNumber(item.value),
        color: getColor(item, index),
      }))
    })

    const total = computed(() => {
      return normalizedItems.value.reduce((sum, item) => sum + item.value, 0)
    })

    const hasValues = computed(() => {
      return total.value > 0
    })

    const positiveItems = computed(() => {
      return normalizedItems.value.filter((item) => item.value > 0)
    })

    const visibleSegmentsCount = computed(() => {
      return positiveItems.value.length
    })

    const segmentSpacing = computed(() => {
      return visibleSegmentsCount.value > 1 ? 2 : 0
    })

    const chartData = computed(() => {
      if (!hasValues.value) {
        return {
          labels: ['Нет данных'],
          values: [1],
          colors: ['#D5DBE2'],
        }
      }

      return {
        labels: normalizedItems.value.map((item) => item.label),
        values: normalizedItems.value.map((item) => item.value),
        colors: normalizedItems.value.map((item) => item.color),
      }
    })

    const destroyChart = () => {
      if (!chartInstance) {
        return
      }

      chartInstance.destroy()
      chartInstance = null
    }

    const renderChart = async () => {
      await nextTick()

      if (!chartCanvas.value) {
        return
      }

      destroyChart()

      chartInstance = new Chart(chartCanvas.value, {
        type: 'doughnut',
        data: {
          labels: chartData.value.labels,
          datasets: [
            {
              data: chartData.value.values,
              backgroundColor: chartData.value.colors,
              borderWidth: 0,
              borderRadius: 0,
              spacing: hasValues.value ? segmentSpacing.value : 0,
              hoverOffset: 0,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          cutout: '64%',
          animation: false,
          layout: {
            padding: 0,
          },
          plugins: {
            legend: {
              display: false,
            },
            tooltip: {
              enabled: hasValues.value,
              displayColors: true,
              callbacks: {
                label: (context) => `${context.label}: ${context.parsed} чел.`,
              },
            },
            datalabels: {
              display: false,
            },
          },
        },
      })
    }

    onMounted(() => {
      renderChart()
    })

    onBeforeUnmount(() => {
      destroyChart()
    })

    watch(
        chartData,
        () => {
          renderChart()
        },
        { deep: true }
    )

    return {
      chartCanvas,
    }
  },
}
</script>

<style scoped lang="scss">
.domains-donut {
  width: 160px;
  height: 140px;
  flex: 0 0 160px;
}
</style>